"use client";

import React from "react";
import { Mic2, Award, BadgeCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const qualityFeatures = [{
  title: "Analog Signal Path",
  description: "Pristine audio capture through our curated collection of vintage preamps and high-end converters.",
  icon: Mic2
}, {
  title: "Precision Monitoring",
  description: "Acoustically treated suites paired with industry-standard monitors for absolute sonic accuracy.",
  icon: Award
}, {
  title: "Mastering Grade",
  description: "Finalize your sound with outboard gear designed for depth, width, and commercial-ready impact.",
  icon: BadgeCheck
}];
export default function TheFreshnessFactor() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="227899" id="the-gear-vault" className="bg-background py-16 md:py-24 overflow-hidden text-foreground" ref={sectionRef}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {}
          <div ref={imageRef} className={cn("relative w-full h-[500px] lg:h-[700px] rounded-2xl overflow-hidden shadow-4xl transition-all duration-1000 ease-out transform", isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8")}>
            <div className="absolute inset-0 bg-primary/20 z-10 mix-blend-overlay"></div>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/pulsewave_studio_rack_gear.png" alt="High-end outboard gear in a professional recording studio" className="w-full h-full object-cover contrast-110 saturate-50 brightness-75" />
            {}
            <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-background to-transparent z-20"></div>
          </div>

          {}
          <div className={cn("flex flex-col gap-8 transition-all duration-1000 delay-300 ease-out transform", isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
            <div className="space-y-4">
              <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground tracking-tighter">
                The Gear Vault
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl font-default">
                At PulseWave Studios, we bridge the gap between classic analog warmth and modern digital precision. Our vault houses the tools that define the sound of modern music.
              </p>
            </div>

            <div className="grid gap-5">
              {qualityFeatures.map((feature, i) => <Card key={i} data-index={i} className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-3xl border-border bg-card/40 backdrop-blur-md rounded-2xl">
                  {}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <CardHeader className="flex flex-row items-start gap-4 pb-2 relative z-10">
                    <div className="mt-1 p-3 rounded-xl bg-secondary text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground shadow-sm">
                      <feature.icon className="w-6 h-6" />
                    </div>
                    <div className="space-y-1">
                      <CardTitle className="text-xl font-bold font-default text-foreground">
                        {feature.title}
                      </CardTitle>
                      <CardContent className="p-0">
                        <p className="text-muted-foreground leading-relaxed font-mono text-sm">
                          {feature.description}
                        </p>
                      </CardContent>
                    </div>
                  </CardHeader>
                </Card>)}
            </div>
          </div>

        </div>
      </div>
    </section>;
}